import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Input,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Delete24Regular,
  PersonDelete24Regular,
  Person24Regular,
  MoreHorizontal24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { authApi, AdminUser } from '../services/api.ts';
import PageHeader from '../components/PageHeader.tsx';
import StatCard from '../components/StatCard.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import EmptyState from '../components/EmptyState.tsx';
import Pagination from '../components/Pagination.tsx';
import ConfirmDialog from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--space-md)',
    marginBottom: 'var(--space-lg)',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },
  table: {
    minWidth: '680px',
    [MOBILE]: {
      minWidth: 0,
    },
  },
  hideOnMobile: {
    [MOBILE]: {
      display: 'none',
    },
  },
  nameCell: {
    [MOBILE]: {
      cursor: 'pointer',
    },
  },
  expandHint: {
    display: 'none',
    [MOBILE]: {
      display: 'inline',
      marginLeft: 'var(--space-xs)',
      color: 'var(--color-text-tertiary)',
      fontSize: '12px',
    },
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-lg)',
    zIndex: 1000,
    animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
    animationDuration: '0.18s',
    '@media (min-width: 769px)': {
      display: 'none',
    },
  },
  modalPanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: 'var(--space-lg)',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '85vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: '0.2s',
    animationTimingFunction: 'ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 'var(--space-sm)',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-sm)',
  },
  detailMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-xs)',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
  },
});

function useIsMobile(): boolean {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const AdminManagement: React.FC = () => {
  const s = useStyles();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [mobileAdmin, setMobileAdmin] = useState<AdminUser | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [pageSize]);
  const isMobile = useIsMobile();
  const [confirmAction, setConfirmAction] = useState<{ type: string; admin: AdminUser } | null>(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAdminFullName, setNewAdminFullName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const { data: admins, isLoading, error } = useQuery({ queryKey: ['admins'], queryFn: authApi.listAdmins });

  const createAdminMutation = useMutation({
    mutationFn: authApi.createAdmin,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admins'] }); setShowCreateDialog(false); setNewAdminEmail(''); setNewAdminPassword(''); setNewAdminFullName(''); toast.success('Admin created'); },
    onError: () => toast.error('Failed to create admin'),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: authApi.toggleAdminStatus,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admins'] }); toast.success('Admin status updated'); },
    onError: () => toast.error('Failed to update admin status'),
  });

  const deleteAdminMutation = useMutation({
    mutationFn: authApi.deleteAdmin,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admins'] }); toast.success('Admin deleted'); },
    onError: () => toast.error('Failed to delete admin'),
  });

  const handleCreateAdmin = async () => {
    if (!newAdminEmail.trim() || !newAdminPassword.trim() || !newAdminFullName.trim()) return;
    setIsCreating(true);
    try { await createAdminMutation.mutateAsync({ email: newAdminEmail, password: newAdminPassword, full_name: newAdminFullName }); }
    finally { setIsCreating(false); }
  };

  const executeConfirmAction = async () => {
    if (!confirmAction) return;
    const { type, admin } = confirmAction;
    if (type === 'toggle') await toggleStatusMutation.mutateAsync(admin.id);
    else if (type === 'delete') await deleteAdminMutation.mutateAsync(admin.id);
    setConfirmAction(null);
  };

  const adminList = admins ?? [];
  const totalAdmins = adminList.length;

  // If the current page falls off the end (e.g. after deletes), step back to the new last page.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalAdmins / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [totalAdmins, pageSize, page]);

  const pagedAdmins = adminList.slice((page - 1) * pageSize, page * pageSize);

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}><Spinner size="large" /></div>;
  if (error) return <EmptyState title="Error loading admins" description="Please try again later" />;

  return (
    <div>
      <PageHeader
        title="Admin Management"
        subtitle="Manage platform administrators"
        actions={
          <Dialog open={showCreateDialog} onOpenChange={(_, data) => setShowCreateDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="primary" icon={<Add24Regular />}>Add Admin</Button>
            </DialogTrigger>
            <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
              <DialogBody>
                <DialogTitle>Create New Admin</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                    <Field label="Full Name" required><Input value={newAdminFullName} onChange={(_, data) => setNewAdminFullName(data.value)} placeholder="Enter full name" /></Field>
                    <Field label="Email" required><Input type="email" value={newAdminEmail} onChange={(_, data) => setNewAdminEmail(data.value)} placeholder="Enter email address" /></Field>
                    <Field label="Password" required><Input type="password" value={newAdminPassword} onChange={(_, data) => setNewAdminPassword(data.value)} placeholder="Enter password" /></Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                  <Button appearance="primary" onClick={handleCreateAdmin} disabled={isCreating || !newAdminEmail.trim() || !newAdminPassword.trim() || !newAdminFullName.trim()}>
                    {isCreating ? 'Creating...' : 'Create Admin'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        }
      />

      <div className={s.statsGrid}>
        <StatCard title="Total Admins" value={admins?.length || 0} />
        <StatCard title="Active" value={admins?.filter(a => a.is_active).length || 0} color="var(--color-success)" />
        <StatCard title="Inactive" value={admins?.filter(a => !a.is_active).length || 0} color="var(--color-error)" />
      </div>

      <div className={s.card}>
        <div className="table-scroll">
        <Table className={s.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Full Name</TableHeaderCell>
              <TableHeaderCell className={s.hideOnMobile}>Email</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell className={s.hideOnMobile}>Created</TableHeaderCell>
              <TableHeaderCell className={s.hideOnMobile} style={{ width: '80px' }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedAdmins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell
                  className={s.nameCell}
                  onClick={isMobile ? () => setMobileAdmin(admin) : undefined}
                >
                  <Text weight="medium">{admin.full_name}</Text>
                  <span className={s.expandHint}>›</span>
                </TableCell>
                <TableCell className={s.hideOnMobile}><Text size={300}>{admin.email}</Text></TableCell>
                <TableCell><StatusBadge status={admin.is_active ? 'active' : 'inactive'} /></TableCell>
                <TableCell className={s.hideOnMobile}><Text size={300}>{new Date(admin.created_at).toLocaleDateString()}</Text></TableCell>
                <TableCell className={s.hideOnMobile}>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button appearance="subtle" icon={<MoreHorizontal24Regular />} size="small" />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem icon={admin.is_active ? <PersonDelete24Regular /> : <Person24Regular />} onClick={() => setConfirmAction({ type: 'toggle', admin })}>
                          {admin.is_active ? 'Deactivate' : 'Activate'}
                        </MenuItem>
                        <MenuItem icon={<Delete24Regular />} onClick={() => setConfirmAction({ type: 'delete', admin })}>Delete</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        <Pagination total={adminList.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }} />
      </div>

      {/* Mobile-only admin details popup */}
      {mobileAdmin && (
        <div className={s.modalOverlay} onClick={() => setMobileAdmin(null)}>
          <div className={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <Text weight="semibold" size={400}>{mobileAdmin.full_name}</Text>
              <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={() => setMobileAdmin(null)} />
            </div>
            <div><StatusBadge status={mobileAdmin.is_active ? 'active' : 'inactive'} /></div>
            <div className={s.detailMeta}>
              <span>Email: {mobileAdmin.email}</span>
              <span>Created: {new Date(mobileAdmin.created_at).toLocaleDateString()}</span>
            </div>
            <div className={s.modalActions}>
              <Button appearance="secondary" icon={mobileAdmin.is_active ? <PersonDelete24Regular /> : <Person24Regular />} onClick={() => { const a = mobileAdmin; setMobileAdmin(null); setConfirmAction({ type: 'toggle', admin: a }); }}>
                {mobileAdmin.is_active ? 'Deactivate' : 'Activate'}
              </Button>
              <Button appearance="secondary" icon={<Delete24Regular />} onClick={() => { const a = mobileAdmin; setMobileAdmin(null); setConfirmAction({ type: 'delete', admin: a }); }} style={{ color: 'var(--color-error)' }}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={executeConfirmAction}
        title={confirmAction?.type === 'delete' ? 'Delete Admin' : (confirmAction?.admin?.is_active ? 'Deactivate Admin' : 'Activate Admin')}
        message={
          confirmAction?.type === 'delete'
            ? `Are you sure you want to delete admin "${confirmAction?.admin?.full_name}"? This cannot be undone.`
            : `Are you sure you want to ${confirmAction?.admin?.is_active ? 'deactivate' : 'activate'} "${confirmAction?.admin?.full_name}"?`
        }
        confirmLabel={confirmAction?.type === 'delete' ? 'Delete' : (confirmAction?.admin?.is_active ? 'Deactivate' : 'Activate')}
        danger={confirmAction?.type === 'delete'}
      />
    </div>
  );
};

export default AdminManagement;
